import { useTranslation } from "react-i18next";
import { Languages } from "./icons";
import { useState, useRef, useEffect } from "react";

interface LanguageSwitcherProps {
  /**
   * "light" (default) is the original styling, used across the
   * authenticated app chrome (light surfaces). "dark" is additive, for the
   * EPIC-38 public homepage's dark hero header — same behavior, same
   * `data-testid`s, no change to any existing caller since it's opt-in.
   */
  variant?: "light" | "dark";
  /**
   * EPIC-106 (PMA-PUB-023): opt-in 44x44 px minimum tap target for the
   * public landing header. Off by default, so the app header (Layout.tsx)
   * and GuestWaiting keep their existing size.
   */
  touchTarget?: boolean;
}

export default function LanguageSwitcher({
  variant = "light",
  touchTarget = false,
}: LanguageSwitcherProps = {}) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDark = variant === "dark";

  const languages = [
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "en", name: "English", flag: "🇺🇸" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLanguage = async (languageCode: string) => {
    console.log("Changing language to:", languageCode);
    await i18n.changeLanguage(languageCode);
    console.log("Language changed to:", i18n.language);
    setIsOpen(false);
    // Save preference to localStorage
    localStorage.setItem("preferredLanguage", languageCode);
    // Force a re-render of all components
    window.dispatchEvent(new Event("languageChanged"));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef} data-testid="language-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          touchTarget ? "min-h-[44px] min-w-[44px] justify-center" : ""
        } ${
          isDark
            ? "text-slate-300 hover:text-slate-100 hover:bg-white/10"
            : "text-gray-700 hover:bg-gray-100"
        }`}
        aria-label="Change language"
        data-testid="language-switcher-toggle"
      >
        <Languages size={20} />
        <span className="hidden sm:inline text-sm font-medium">
          {currentLanguage.flag} {currentLanguage.name}
        </span>
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border py-1 z-50 ${
            isDark
              ? "bg-surface-dark-card border-surface-dark-border"
              : "bg-white border-gray-200"
          }`}
          data-testid="language-switcher-dropdown"
        >
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              data-testid={`language-option-${language.code}`}
              className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                isDark
                  ? i18n.language === language.code
                    ? "bg-white/10 text-slate-100 font-medium"
                    : "text-slate-300 hover:bg-white/5"
                  : i18n.language === language.code
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="text-xl">{language.flag}</span>
              <span>{language.name}</span>
              {i18n.language === language.code && (
                <span
                  className={`ml-auto ${isDark ? "text-slate-100" : "text-primary-600"}`}
                >
                  ✓
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
